/**
 * Foto — DESKTOP.
 *
 * Tres columnas: la imagen, los campos editables y **el JSON crudo que devolvió el modelo con la
 * confianza de cada campo**. Esa tercera columna es la razón de ser de esta variante: en el
 * celular no cabe, pero en el PC es donde se audita de verdad por qué el OCR se equivocó — §4.5.4
 * pide guardar el crudo para poder revisar errores después, y acá se puede ver en el momento.
 *
 * Además acepta arrastrar el archivo, que en un teléfono no significa nada.
 */

import { useRef, useState, type ReactNode } from 'react';
import { formatearNumero, type Condiciones } from '@mg/core';

import { useIngesta } from '../features/useIngesta.ts';
import { Aviso, Campo, Icono, Insignia, Selector, Vacio } from '../ui/componentes.tsx';
import { useRuta } from '../ui/router.tsx';

export function IngestaDesktop(): ReactNode {
  const g = useIngesta();
  const { ir } = useRuta();
  const input = useRef<HTMLInputElement>(null);
  const [arrastrando, setArrastrando] = useState(false);

  if (g.datos.cargando) return <Vacio>Cargando…</Vacio>;

  return (
    <>
      <div className="fila fila--entre">
        <div className="fila" style={{ gap: 'var(--e2)' }}>
          <button
            type="button"
            className={g.tipo === 'balanza' ? 'boton boton--chico boton--principal' : 'boton boton--chico'}
            onClick={() => {
              g.reiniciar();
              g.setTipo('balanza');
            }}
          >
            Balanza
          </button>
          <button
            type="button"
            className={g.tipo === 'consola_cardio' ? 'boton boton--chico boton--principal' : 'boton boton--chico'}
            onClick={() => {
              g.reiniciar();
              g.setTipo('consola_cardio');
            }}
          >
            Consola de cardio
          </button>
        </div>

        {g.respuesta && (
          <span className="nota--fina">
            modelo: <span className="lectura">{g.respuesta.modelo}</span> · confianza global{' '}
            <span className="lectura">{formatearNumero(g.respuesta.lectura.confianza_global * 100, 0)} %</span>
          </span>
        )}
      </div>

      {/* Que falte la sesión no bloquea nada, pero descubrirlo al confirmar sí desconcierta. */}
      {g.abriraSesion && g.paso !== 'listo' && (
        <Aviso tono="info">
          El cardio se guarda en la sesión de hoy. No hay una abierta: se abre sola al confirmar.
        </Aviso>
      )}

      {g.paso === 'listo' && (
        <Aviso tono="info" titulo="Guardado">
          {g.tipo === 'balanza'
            ? 'La medición quedó registrada, con el JSON crudo guardado para auditoría.'
            : 'El cardio quedó asociado a la sesión de hoy.'}{' '}
          <button type="button" className="boton boton--chico" style={{ marginTop: 8 }} onClick={g.reiniciar}>
            Cargar otra
          </button>
        </Aviso>
      )}

      {(g.paso === 'elegir' || g.paso === 'leyendo') && (
        <section
          className="tarjeta"
          onDragOver={(e) => {
            e.preventDefault();
            setArrastrando(true);
          }}
          onDragLeave={() => setArrastrando(false)}
          onDrop={(e) => {
            e.preventDefault();
            setArrastrando(false);
            const f = e.dataTransfer.files?.[0];
            if (f) void g.leer(f);
          }}
          style={{
            display: 'grid',
            placeItems: 'center',
            minHeight: 300,
            borderStyle: 'dashed',
            borderColor: arrastrando ? 'var(--ambar)' : 'var(--hierro-borde)',
            background: arrastrando ? 'var(--ambar-tenue)' : undefined,
            textAlign: 'center',
            gap: 'var(--e3)',
          }}
        >
          {g.paso === 'leyendo' ? (
            <>
              {g.vistaPrevia && (
                <img
                  src={g.vistaPrevia}
                  alt="La foto que se está leyendo"
                  style={{ maxHeight: 220, borderRadius: 'var(--r)' }}
                />
              )}
              <p className="nota">Leyendo la pantalla…</p>
            </>
          ) : (
            <>
              <Icono nombre="camara" tamano={34} />
              <div>
                <p style={{ fontWeight: 600 }}>Arrastrá la foto acá</p>
                <p className="nota--fina">
                  {g.tipo === 'balanza'
                    ? 'La pantalla de la balanza, de frente y sin reflejos.'
                    : 'La consola de la máquina. Los siete segmentos se leen mal: vas a revisar los números.'}
                </p>
              </div>
              <input
                ref={input}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void g.leer(f);
                }}
              />
              <button type="button" className="boton" onClick={() => input.current?.click()}>
                Elegir un archivo
              </button>
              <p className="nota--fina">Vas a poder revisar y corregir los números antes de guardar.</p>
            </>
          )}
          {g.error && (
            <Aviso tono="importante">
              {g.error}
              {/* Sin red no hay nada que reintentar solo: la salida es cargarlo a mano. */}
              {g.sinRed && g.tipo === 'balanza' && (
                <div style={{ marginTop: 'var(--e2)' }}>
                  <button type="button" className="boton boton--chico" onClick={() => ir('cuerpo', 'medir')}>
                    Cargarla a mano
                  </button>
                </div>
              )}
              {g.sinRed && g.tipo === 'consola_cardio' && (
                <p style={{ marginTop: 'var(--e2)' }}>
                  La consola sólo se lee desde la foto: probá de nuevo cuando vuelva la señal.
                </p>
              )}
            </Aviso>
          )}
        </section>
      )}

      {g.paso === 'revisar' && (
        <div className="d-rejilla" style={{ gridTemplateColumns: '300px minmax(0, 1fr) 320px', alignItems: 'start' }}>
          <section className="tarjeta">
            <span className="eyebrow">La foto</span>
            {g.vistaPrevia && (
              <img
                src={g.vistaPrevia}
                alt="La foto cargada"
                style={{ width: '100%', marginTop: 'var(--e2)', borderRadius: 'var(--r)' }}
              />
            )}
            <button type="button" className="boton boton--chico boton--fantasma" style={{ marginTop: 'var(--e3)' }} onClick={g.reiniciar}>
              Descartar
            </button>
            <p className="nota--fina" style={{ marginTop: 'var(--e3)' }}>
              La imagen no se guarda. Lo que queda auditable es lo que el modelo leyó.
            </p>
          </section>

          <section className="tarjeta">
            <span className="eyebrow">Revisá y confirmá</span>
            <p className="nota" style={{ marginTop: 4, marginBottom: 'var(--e4)' }}>
              Nada se guarda hasta que confirmes. Los campos en ámbar son los que el modelo leyó con
              poca confianza.
            </p>

            {g.tipo === 'balanza' && g.balanza && <CamposBalanzaDesktop g={g} />}
            {g.tipo === 'consola_cardio' && g.cardio && <CamposCardioDesktop g={g} />}

            {g.errores.length > 0 && (
              <div style={{ marginTop: 'var(--e4)' }}>
                <Aviso tono="importante" titulo="Eso no se puede guardar">
                  {g.errores.map((h) => h.mensaje).join(' ')}
                </Aviso>
              </div>
            )}

            {g.errores.length === 0 && g.aConfirmar.length > 0 && (
              <div style={{ marginTop: 'var(--e4)' }}>
                <Aviso tono="atencion">
                  {g.aConfirmar.map((h) => h.mensaje).join(' ')}
                  <label className="fila" style={{ marginTop: 8, gap: 8 }}>
                    <input type="checkbox" checked={g.confirmado} onChange={(e) => g.setConfirmado(e.target.checked)} />
                    <span>Es correcto</span>
                  </label>
                </Aviso>
              </div>
            )}

            {g.faltaNivel && (
              <div style={{ marginTop: 'var(--e4)' }}>
                <Aviso tono="atencion" titulo="Falta el nivel de resistencia">
                  No aparece en la foto. Sin él, 3,23 km contra 2,80 km no dice nada: puede ser un
                  nivel más bajo, no mejor estado físico.
                </Aviso>
              </div>
            )}

            <button
              type="button"
              className="boton boton--principal"
              style={{ marginTop: 'var(--e4)', width: '100%' }}
              disabled={!g.puedeGuardar || g.guardando}
              onClick={() => void g.confirmar()}
            >
              {g.guardando ? 'Guardando…' : 'Confirmar y guardar'}
            </button>

            {g.error && (
              <div style={{ marginTop: 'var(--e3)' }}>
                <Aviso tono="importante">{g.error}</Aviso>
              </div>
            )}
          </section>

          <section className="tarjeta">
            <span className="eyebrow">Lo que devolvió el modelo</span>
            <table className="tabla" style={{ marginTop: 'var(--e2)' }}>
              <thead>
                <tr>
                  <th>Campo</th>
                  <th className="num">Valor</th>
                  <th className="num">Confianza</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(g.respuesta?.lectura.campos ?? {}).map(([k, v]) => (
                  <tr key={k}>
                    <td style={{ fontSize: 12 }}>{k}</td>
                    <td className="num">{v.valor === null ? '—' : formatearNumero(v.valor, 2)}</td>
                    <td className="num" style={{ color: g.revisar.has(k) ? 'var(--ambar)' : undefined }}>
                      {formatearNumero(v.confianza * 100, 0)} %
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {g.noLegibles.length > 0 && (
              <p className="nota--fina" style={{ marginTop: 'var(--e3)' }}>
                No pudo leer: {g.noLegibles.join(', ')}. <Insignia tono="ambar">no inventó nada</Insignia>
              </p>
            )}

            <details style={{ marginTop: 'var(--e3)' }}>
              <summary className="nota--fina" style={{ cursor: 'pointer' }}>
                JSON crudo
              </summary>
              <pre
                className="lectura"
                style={{
                  fontSize: 11,
                  overflowX: 'auto',
                  background: 'var(--hierro-hundido)',
                  padding: 'var(--e2)',
                  borderRadius: 'var(--r-chico)',
                  marginTop: 6,
                }}
              >
                {JSON.stringify(JSON.parse(g.respuesta?.jsonCrudo ?? '{}'), null, 2)}
              </pre>
            </details>
          </section>
        </div>
      )}
    </>
  );
}

function CamposBalanzaDesktop({ g }: { g: ReturnType<typeof useIngesta> }): ReactNode {
  const b = g.balanza!;
  const set = (k: keyof typeof b, v: string) => g.setBalanza({ ...b, [k]: v });
  const campos: [keyof typeof b, string, string][] = [
    ['peso', 'Peso (kg)', 'peso'],
    ['grasa_pct', 'Grasa %', 'grasa_pct'],
    ['masa_magra', 'Masa magra', 'masa_magra'],
    ['masa_muscular', 'Muscular', 'masa_muscular'],
    ['masa_osea', 'Ósea', 'masa_osea'],
    ['agua_pct', 'Agua %', 'agua_pct'],
    ['grasa_visceral', 'Visceral', 'grasa_visceral'],
    ['imc', 'IMC', 'imc'],
    ['tmb', 'TMB', 'tmb'],
  ];

  return (
    <>
      <div className="d-rejilla d-rejilla--3" style={{ gap: 'var(--e3)' }}>
        <Campo etiqueta="Fecha" texto type="date" value={g.fecha} onChange={(e) => g.setFecha(e.target.value)} />
        <Campo etiqueta="Hora" texto type="time" value={g.hora} onChange={(e) => g.setHora(e.target.value)} />
        <Selector
          etiqueta="Condiciones"
          value={g.condiciones}
          onChange={(e) => g.setCondiciones(e.target.value as Condiciones)}
        >
          <option value="ayuno">En ayunas</option>
          <option value="post_entreno">Después de entrenar</option>
          <option value="desconocido">Sin registrar</option>
        </Selector>
      </div>

      <div className="d-rejilla d-rejilla--3" style={{ gap: 'var(--e3)', marginTop: 'var(--e4)' }}>
        {campos.map(([clave, etiqueta, campoOCR]) => (
          <Campo
            key={clave}
            etiqueta={etiqueta}
            inputMode="decimal"
            revisar={g.revisar.has(campoOCR)}
            value={b[clave]}
            onChange={(e) => set(clave, e.target.value)}
          />
        ))}
      </div>
    </>
  );
}

function CamposCardioDesktop({ g }: { g: ReturnType<typeof useIngesta> }): ReactNode {
  const c = g.cardio!;
  const set = (k: keyof typeof c, v: string) => g.setCardio({ ...c, [k]: v });

  return (
    <div className="d-rejilla d-rejilla--2" style={{ gap: 'var(--e3)' }}>
      <Campo
        etiqueta="Tiempo (mm:ss)"
        revisar={g.revisar.has('duracion_seg')}
        value={c.duracion}
        onChange={(e) => set('duracion', e.target.value)}
      />
      <Campo
        etiqueta="Distancia (km)"
        inputMode="decimal"
        revisar={g.revisar.has('distancia_km')}
        value={c.distancia_km}
        onChange={(e) => set('distancia_km', e.target.value)}
      />
      <Campo
        etiqueta="Calorías"
        inputMode="numeric"
        revisar={g.revisar.has('calorias')}
        value={c.calorias}
        onChange={(e) => set('calorias', e.target.value)}
      />
      <Campo
        etiqueta="Nivel de resistencia"
        inputMode="numeric"
        revisar={g.faltaNivel}
        value={c.nivel_resistencia}
        onChange={(e) => set('nivel_resistencia', e.target.value)}
        ayuda="Obligatorio: sin esto la sesión no es comparable."
      />
      <Campo etiqueta="Máquina" texto value={c.maquina_tipo} onChange={(e) => set('maquina_tipo', e.target.value)} />
    </div>
  );
}
